import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { resolveLogoutDestination } from "@/contexts/logoutDestination";
import { decideAuthFailure } from "@/contexts/authFailure";

const AuthContext = createContext(null);

// Storage keys for the multi-slot session model.
//   ACTIVE_TOKEN / ACTIVE_USER are read by the axios interceptor and
//   represent whoever is "logged in right now" (admin OR driver).
//   When admin links a driver, the driver's credentials are stored separately
//   under LINKED_DRIVER_*. When admin enters driver mode, the admin session
//   is parked under LINKED_ADMIN_* so they can come back to it without
//   re-typing a password — and crucially without ever broadcasting a global
//   logout.
const ACTIVE_TOKEN = "sdrive_token";
const ACTIVE_USER  = "sdrive_user";
const LINKED_ADMIN_TOKEN  = "sdrive_linked_admin_token";
const LINKED_ADMIN_USER   = "sdrive_linked_admin_user";
const LINKED_DRIVER_TOKEN = "sdrive_linked_driver_token";
const LINKED_DRIVER_USER  = "sdrive_linked_driver_user";

const readJSON = (k) => {
  try { return JSON.parse(localStorage.getItem(k) || "null"); } catch { return null; }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Public-facing slots so UI can render "linked driver" / "linked admin" cues.
  const [linkedDriver, setLinkedDriver] = useState(readJSON(LINKED_DRIVER_USER));
  const [linkedAdmin,  setLinkedAdmin]  = useState(readJSON(LINKED_ADMIN_USER));

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem(ACTIVE_TOKEN);
    if (!token) { setLoading(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      // Phase N1A — Only clear the token on a real authentication
      // failure (401). Network errors / timeouts / 5xx responses must
      // NOT log the user out: those are transient runtime issues
      // (backend restart, mobile dropping signal, brief outage) and
      // wiping credentials in that case would falsely show the user
      // "Invalid credentials" the next time they try to log back in.
      //
      // Decision logic is isolated in `decideAuthFailure` so it can
      // be unit-tested without rendering this whole context.
      const decision = decideAuthFailure(err);
      if (decision.action === "clear_session") {
        localStorage.removeItem(ACTIVE_TOKEN);
        localStorage.removeItem(ACTIVE_USER);
      } else {
        // Best-effort restore from the cached snapshot so the UI does
        // not flip to "logged out" while the backend is unreachable.
        const cached = readJSON(ACTIVE_USER);
        if (cached) setUser(cached);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem(ACTIVE_TOKEN, data.access_token);
    localStorage.setItem(ACTIVE_USER, JSON.stringify(data));
    setUser(data);
    return data;
  };

  const logout = async () => {
    // 1. Best-effort backend logout (ignore failure: we still want the
    //    client to log out locally even if the network is dead).
    try { await api.post("/auth/logout"); } catch {}

    // 2. SNAPSHOT everything we need to decide the redirect BEFORE we
    //    mutate any React state or local/session storage. Order matters:
    //    once `setUser(null)` runs, <ProtectedRoute> will schedule a
    //    <Navigate to="/" replace /> on the next render, which can race
    //    with our redirect if it depends on data we've already wiped.
    const roleAtLogout = (user && user.role) || null;
    const userSnapshot = user;
    let sessionSlug = null;
    try { sessionSlug = sessionStorage.getItem("sdrive_tenant_slug"); } catch {}

    // 3. Compute destination via the pure helper (unit-tested in
    //    contexts/__tests__/logoutDestination.test.js). Helper already
    //    validates the slug (string, trim, no "/", not "undefined"/
    //    "null", regex-matched) and falls back to "/" when invalid.
    const destination = resolveLogoutDestination({
      role:        roleAtLogout,
      sessionSlug,
      user:        userSnapshot,
    });

    // 4. Cleanup. Wipe ALL session slots: leaving a linked driver
    //    attached to a logged-out admin would let the next person who
    //    lands on this device step into someone else's account.
    [ACTIVE_TOKEN, ACTIVE_USER,
     LINKED_ADMIN_TOKEN, LINKED_ADMIN_USER,
     LINKED_DRIVER_TOKEN, LINKED_DRIVER_USER].forEach((k) => localStorage.removeItem(k));
    // Super-admins never re-enter a tenant; clear the slug so a later
    // login as a tenant admin on this tab is not contaminated.
    if (roleAtLogout === "super_admin") {
      try { sessionStorage.removeItem("sdrive_tenant_slug"); } catch {}
    }
    setUser(null);
    setLinkedDriver(null);
    setLinkedAdmin(null);

    // 5. Hard navigation. `window.location.assign(...)` triggers a real
    //    browser load that always supersedes any in-flight React Router
    //    <Navigate to="/"> from <ProtectedRoute>, so we cannot be
    //    overwritten between this point and the page unload.
    try { window.location.assign(destination); } catch { /* SSR / jsdom */ }
  };

  // ─── Linked-driver session control (admin-only flow) ───
  // Store a driver session WITHOUT touching the admin's active session. The
  // backend has no concept of "linked accounts" — these are just two JWTs
  // kept in parallel on the client.
  const linkDriver = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.role !== "driver") {
      throw Object.assign(new Error("not_driver"), { reason: "not_driver" });
    }
    localStorage.setItem(LINKED_DRIVER_TOKEN, data.access_token);
    localStorage.setItem(LINKED_DRIVER_USER, JSON.stringify(data));
    setLinkedDriver(data);
    return data;
  };

  const unlinkDriver = () => {
    localStorage.removeItem(LINKED_DRIVER_TOKEN);
    localStorage.removeItem(LINKED_DRIVER_USER);
    setLinkedDriver(null);
  };

  // Switch the "active" session to the previously-linked driver. The admin
  // session is parked, not destroyed.
  const enterDriverMode = async () => {
    const drvTok = localStorage.getItem(LINKED_DRIVER_TOKEN);
    const drvUsr = readJSON(LINKED_DRIVER_USER);
    if (!drvTok || !drvUsr) throw new Error("No linked driver");

    // Park the current admin session
    const curTok = localStorage.getItem(ACTIVE_TOKEN);
    const curUsr = readJSON(ACTIVE_USER);
    if (curTok && curUsr?.role === "admin") {
      localStorage.setItem(LINKED_ADMIN_TOKEN, curTok);
      localStorage.setItem(LINKED_ADMIN_USER, JSON.stringify(curUsr));
      setLinkedAdmin(curUsr);
    }
    // Swap in the driver
    localStorage.setItem(ACTIVE_TOKEN, drvTok);
    localStorage.setItem(ACTIVE_USER, JSON.stringify(drvUsr));
    // The linked-driver slot stays populated — exitDriverMode reads it back
    setUser(drvUsr);
    return drvUsr;
  };

  // Reverse of enterDriverMode. Driver session is preserved (still linked) —
  // the admin can switch back any time without re-authenticating.
  const exitDriverMode = async () => {
    const adminTok = localStorage.getItem(LINKED_ADMIN_TOKEN);
    const adminUsr = readJSON(LINKED_ADMIN_USER);
    if (!adminTok || !adminUsr) throw new Error("No linked admin");

    localStorage.setItem(ACTIVE_TOKEN, adminTok);
    localStorage.setItem(ACTIVE_USER, JSON.stringify(adminUsr));
    localStorage.removeItem(LINKED_ADMIN_TOKEN);
    localStorage.removeItem(LINKED_ADMIN_USER);
    setLinkedAdmin(null);
    setUser(adminUsr);
    return adminUsr;
  };

  return (
    <AuthContext.Provider value={{
      user, loading, login, logout, setUser,
      linkedDriver, linkedAdmin,
      linkDriver, unlinkDriver,
      enterDriverMode, exitDriverMode,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
